import { isNumeric } from "./format";
import { getCellIndex, getCellNameByIndex, getCellsArray, isRangeId } from "./main";
import { Store } from "@xbs/muon";
import { addDay, addTime, dateDiff, excelDateToJSDate, getExcelDateFromJs, timeDiff } from "./date";
import { IGrid } from "@dhx/ts-grid";
import { ISpan } from "../types";

export function getCellsDiff(
	firstCell: string,
	lastCell: string,
	grid: IGrid
): { x: number; y: number; isLargerByX: boolean, negativeDir: boolean } {
	const firstCellIndex = getCellIndex(firstCell, grid);
	const lastCellIndex = getCellIndex(lastCell, grid);

	const x = lastCellIndex.col - firstCellIndex.col;
	const y = lastCellIndex.row - firstCellIndex.row;
	const negativeDir = x < 0 || y < 0;

	return {
		x,
		y,
		isLargerByX: Math.abs(x) > Math.abs(y),
		negativeDir,
	};
}

export function getLastCopyingCell(firstCell: string, lastCell: string, grid: IGrid): string {
	const isRange = isRangeId(firstCell);
	if (isRange) {
		firstCell = firstCell.split(":")[1];
	}
	const firstCellIndex = getCellIndex(firstCell, grid);
	const { x, y, isLargerByX } = getCellsDiff(firstCell, lastCell, grid);

	const nextY = firstCellIndex.row + y;
	const nextX = firstCellIndex.col + x;

	const col = isLargerByX ? nextX : firstCellIndex.col;
	const row = isLargerByX ? firstCellIndex.row : nextY;

	return getCellNameByIndex(row, col, grid);
}

export function getProgressionStep(progression: any[]): number {
	const items = progression.map(item => parseFloat(item) || 0);
	const sum = items.reduce((total: number, elem: number) => total + elem, 0);
	const len = items.length;
	return (2 * (sum / len) - 2 * items[0]) / (len - 1);
}

export function getDateStep(dates: any[]): number {
	let diff = dateDiff(dates[0], dates[1]);
	for (let i = 1; i < dates.length - 1; i++) {
		if (dateDiff(dates[i], dates[i + 1]) !== diff) {
			diff = 0;
			i = dates.length;
		}
	}
	return diff;
}

export function getTimeStep(dates: any[]): number {
	let diff = timeDiff(dates[0], dates[1]);
	for (let i = 1; i < dates.length - 1; i++) {
		if (timeDiff(dates[i], dates[i + 1]) !== diff) {
			diff = 0;
			i = dates.length;
		}
	}
	return diff;
}

export function isCellGreater(a: string, b: string, grid): boolean {
	const aa = getCellIndex(a, grid);
	const bb = getCellIndex(b, grid);
	if (aa.row === bb.row) {
		return aa.col > bb.col;
	} else {
		return aa.row > bb.row;
	}
}

export function getAutoFilledCells(
	values: any[],
	focused: string,
	selected: string,
	math: Store,
	grid: IGrid
): { cells: string; value: any[], spans } {
	const numbers = values.filter(item => isNumeric(item.value) && item.format.id !== "date" && item.format.id !== "time" && item.format.id !== "text" ).map(i => i.value);
	const dates = values.filter(item => isNumeric(item.value) && item.format.id === "date" ).map(i => excelDateToJSDate(i.value));
	const times = values.filter(item => isNumeric(item.value) && item.format.id === "time" ).map(i => excelDateToJSDate(i.value));
	if (numbers.length === 1) {
		numbers.push(+numbers[0] + 1);
	}
	let dateStep = dates.length < 2 ? 1 : getDateStep(dates);
	let timeStep = times.length < 2 ? 3600 * 1000 : getTimeStep(times);
	const focusedCellsArr = getCellsArray(focused, grid);
	let step = getProgressionStep(numbers);

	const isCellGreater = (a: string, b: string): boolean => {
		const aa = getCellIndex(a, grid);
		const bb = getCellIndex(b, grid);
		if (aa.row === bb.row) {
			return aa.col > bb.col;
		} else {
			return aa.row > bb.row;
		}
	};
	const spans: string[] = [];

	let last = parseFloat(numbers[numbers.length - 1]);
	let lastDate = dates[dates.length - 1];
	let lastTime = times[times.length - 1];
	let current = 0;

	const [focusedFirst, focusedLast] = focused.split(":");
	const [selectedFirst, selectedLast] = selected.split(":");
	const largerByX = getCellsDiff(focusedFirst === selectedFirst && focusedLast ? focusedLast : focusedFirst, selectedFirst, grid)
		.isLargerByX;
	const negativeDir = isCellGreater(focusedFirst, isCellGreater(selectedFirst, selectedLast) ? selectedLast : selectedFirst);

	if (negativeDir) {
		dateStep = -dateStep;
		lastDate = dates[0];
		timeStep = -timeStep;
		lastTime = times[0];
		step = getProgressionStep([...numbers.reverse()]);
		last = parseFloat(numbers[numbers.length - 1]);
	}
	let targetCells = getCellsArray(selected, grid).filter(c => !focusedCellsArr.includes(c));
	if(negativeDir) {
		targetCells = targetCells.reverse();
		values = values.reverse();
	}

	let res = targetCells.map((c, index) => {
		let newVal;
		let isMath;
		isMath = values[current].value.toString().startsWith("=");
		newVal = values[current].value;
		const span = values[current].span;

		if (span) {
			const { from, to } = span as ISpan;
			const index = getCellIndex(c, grid);
			const lastCell = getCellNameByIndex(
				index.row + to.row - from.row,
				index.col + to.column - from.column,
				grid
			);
			const newMerge = `${c}:${lastCell}`;
			if (!spans.includes(newMerge)) {
				spans.push(newMerge);
			}
		}
		if (isMath) {
			let row = 0;
			let col = 0;
			const pos = negativeDir
				? (index + current + focusedCellsArr.length + 1) * -1
				: index - current + focusedCellsArr.length;
			if (largerByX) {
				col = pos;
			} else {
				row = pos;
			}
			newVal = math.transposeMath(newVal, row, col);
		}

		if (isNumeric(newVal)) {
			switch (values[current].format.id) {
				case "date":
					lastDate = addDay(lastDate, dateStep);
					newVal = getExcelDateFromJs(lastDate);
					break;
				case "time":
					lastTime = addTime(lastTime, timeStep);
					newVal = getExcelDateFromJs(lastTime);
					break;
				default:
					last = last + step;
					newVal = last.toFixed(5);
			}
		}

		current = values[current + 1] !== undefined ? current + 1 : 0;
		return newVal;
	});
	if (negativeDir) {
		res = res.reverse();
	}

	return {
		cells: `${targetCells[0]}:${targetCells[targetCells.length - 1]}`,
		value: res,
		spans,
	};
}
